"use client"

import { useEffect, useState, useCallback, useMemo } from "react"
import { PeopleSearchDropdown } from "@/components/people-search-dropdown"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Loader2, Folder, Plus, Compass, User as UserIcon, Search, UserCheck, ArrowDownUp, Users } from "@/lib/icons"
import { CollectionCard } from "./CollectionCard"
import { CollectionFormDialog } from "./CollectionFormDialog"
import { CollectionReviewTags } from "./CollectionReviewTags"
import { useMigrateLegacyFolders } from "@/hooks/use-migrate-legacy-folders"
import { GridColumnsControl } from "@/components/grid-columns-control"
import { useGoonfolioGridColumns } from "@/hooks/use-goonfolio-grid-columns"
import { useAuthStore } from "@/lib/auth-store"
import { cn } from "@/lib/utils"
import type { CollectionListItem } from "./types"
import { useTranslations } from "next-intl"
import { InlineGridAd, injectAdsIntoPosts, type InlineAd } from "@/components/ads/inline-grid-ad"
import { useAdStore } from "@/lib/ad-store"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"

interface GoonfolioTabProps {
  profileUserId: string
  isOwnProfile: boolean
  /**
   * Shown inside someone's profile: only that person's public folios (plus sorting and the grid size), with no
   * Folios/Explore switch. Browsing everyone's folios belongs to the Goonfolio hub, not to a profile.
   */
  profileOnly?: boolean
}

type ExploreSort = "new" | "top" | "rating"

const SORT_OPTIONS: { value: ExploreSort; label: string }[] = [
  { value: "new", label: "New" },
  { value: "top", label: "Top" },
  { value: "rating", label: "Rating" },
]

// "My Folios" sort — client-side, same segmented-button pattern as Favorites (favorites-browser.tsx),
// but sorting on the real created_at/name/itemCount fields the collections API already returns
// instead of array position (Favorites doesn't have a per-item timestamp to sort by, folios do).
type FolioSort = "newest" | "oldest" | "title" | "items"

const FOLIO_SORT_OPTIONS: { value: FolioSort; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "title", label: "Title" },
  { value: "items", label: "Most items" },
]

export function GoonfolioTab({ profileUserId, isOwnProfile, profileOnly = false }: GoonfolioTabProps) {
  const tt = useTranslations("goonfolioTab")
  const tc = useTranslations("communityFolios")
  const { user } = useAuthStore()
  // Explore: only community folios (public folios made for other people to post into)
  const [communityOnly, setCommunityOnly] = useState(false)
  // Which kind of folio the create dialog makes
  const [formKind, setFormKind] = useState<"personal" | "community">("personal")
  const [viewState, setView] = useState<"folios" | "explore">("folios")
  // On a profile there is only the person's own folios; the Explore mode is never reachable.
  const view: "folios" | "explore" = profileOnly ? "folios" : viewState
  const [collections, setCollections] = useState<CollectionListItem[] | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editingCollection, setEditingCollection] = useState<CollectionListItem | null>(null)
  const [ratingCollection, setRatingCollection] = useState<CollectionListItem | null>(null)
  const { gridColumns, setGridColumns, maxColumns } = useGoonfolioGridColumns()

  const [searchInput, setSearchInput] = useState("")
  const [peopleFocused, setPeopleFocused] = useState(false)
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<ExploreSort>("new")
  const [followedOnly, setFollowedOnly] = useState(false)
  const [folioSort, setFolioSort] = useState<FolioSort>("newest")

  // goonfolio_grid_ads — house ads injected into this folio grid, previously
  // configured in admin (enable + every-20 cadence) but rendered nowhere
  // (see the "NOT currently wired to anything" comment on this key in
  // lib/ad-frequency-store.ts). Reuses the exact same house-ad content
  // source (/api/ads/serve) and injectAdsIntoPosts/InlineGridAd mechanism
  // already proven in components/booru-grid.tsx — this is a new injection
  // SITE, not a new ad content type. Deliberately does NOT touch
  // components/collections-feed.tsx's separate goonfolio_feed_ads wiring.
  const { isPremiumUser } = useAdStore()
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const { config, fetchConfig, getFrequency, isEnabled } = useAdFrequencyStore()
  const [liveAds, setLiveAds] = useState<InlineAd[]>([])
  const [dismissedAds, setDismissedAds] = useState<Set<string>>(new Set())

  useEffect(() => { fetchConfig() }, [fetchConfig])

  useEffect(() => {
    if (effectivePremiumUser) return
    const forceAds = isAdTestModeOn() ? "&forceAds=1" : ""
    fetch(`/api/ads/serve?count=10${forceAds}`)
      .then((r) => r.json())
      .then((ads) => { if (Array.isArray(ads)) setLiveAds(ads) })
      .catch(() => {})
  }, [effectivePremiumUser])

  const adsEnabled = config ? isEnabled("goonfolio_grid_ads") : true
  // Same "Force Ads" test-mode override as booru-grid.tsx's dynamicAdFrequency
  // — collapses cadence to 1 so every eligible slot fires immediately,
  // instead of still requiring a real multi-item cadence to be met.
  const adFrequency = isAdTestModeOn() ? 1 : ((config ? getFrequency("goonfolio_grid_ads") : 20) || 20)

  // Debounce the search box so we're not firing a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const load = useCallback(() => {
    setCollections(null)
    let url: string
    if (view === "explore") {
      const params = new URLSearchParams({ scope: "explore", sort })
      if (search) params.set("q", search)
      if (followedOnly) params.set("followedOnly", "true")
      if (communityOnly) params.set("kind", "community")
      url = `/api/collections?${params.toString()}`
    } else {
      url = `/api/collections?userId=${profileUserId}`
    }
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setCollections(data?.collections ?? []))
      .catch(() => setCollections([]))
  }, [view, profileUserId, sort, search, followedOnly, communityOnly])

  useEffect(() => { load() }, [load])

  // "My Folios" only — Explore already arrives pre-sorted from the server via `sort`/the API call.
  const sortedCollections = useMemo(() => {
    if (!collections) return collections
    if (view !== "folios") return collections
    const sorted = [...collections]
    sorted.sort((a, b) => {
      if (folioSort === "oldest") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      if (folioSort === "title") return a.name.localeCompare(b.name)
      if (folioSort === "items") return b.itemCount - a.itemCount
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime() // "newest" (default)
    })
    return sorted
  }, [collections, view, folioSort])

  // Ad-injected view of the grid — only meaningful for "My Folios"/"Explore"
  // grid rendering, never affects `collections`/`sortedCollections` state
  // itself (delete/edit/sort logic below stays untouched).
  const itemsWithAds = useMemo(() => {
    const base = sortedCollections ?? collections
    if (!base) return null
    const items = base.map((c) => ({ type: "post" as const, item: c }))
    if (!adsEnabled || effectivePremiumUser) return items
    const ads = liveAds.filter((ad) => !dismissedAds.has(ad.id))
    if (ads.length === 0 || base.length < adFrequency) return items
    return injectAdsIntoPosts(items, ads, adFrequency)
  }, [sortedCollections, collections, adsEnabled, effectivePremiumUser, liveAds, dismissedAds, adFrequency])

  const handleDismissAd = (adId: string) => {
    setDismissedAds((prev) => new Set([...prev, adId]))
  }

  // One-time transfer of the old localStorage-only "folders" bookmarks into
  // real folios, only relevant for the signed-in owner viewing their own tab.
  useMigrateLegacyFolders(isOwnProfile, load)

  async function handleDelete(id: string) {
    if (!confirm("Delete this folio? This can't be undone.")) return
    await fetch(`/api/collections/${id}`, { method: "DELETE" })
    load()
  }

  return (
    <div className="space-y-4">
      {!profileOnly && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border p-1 gap-1">
            <Button
              size="sm"
              variant={view === "folios" ? "default" : "ghost"}
              onClick={() => setView("folios")}
              className="gap-1.5"
            >
              <UserIcon className="h-3.5 w-3.5" />
              {isOwnProfile ? tt("myFolios") : tt("folios")}
            </Button>
            <Button
              size="sm"
              variant={view === "explore" ? "default" : "ghost"}
              onClick={() => setView("explore")}
              className="gap-1.5"
            >
              <Compass className="h-3.5 w-3.5" />
              {tt("explore")}
            </Button>
          </div>

          <GridColumnsControl gridColumns={gridColumns} setGridColumns={setGridColumns} maxColumns={maxColumns} />

          {isOwnProfile && view === "folios" && (
            <Button
              size="icon"
              title={tt("newFolio")}
              onClick={() => { setEditingCollection(null); setFormKind("personal"); setFormOpen(true) }}
              className="h-8 w-8 rounded-full"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {view === "folios" && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
            <ArrowDownUp className="h-3.5 w-3.5" />
            {tt("sort")}
          </div>
          <div className="inline-flex rounded-lg border p-1 gap-1 shrink-0">
            {FOLIO_SORT_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={folioSort === opt.value ? "default" : "ghost"}
                onClick={() => setFolioSort(opt.value)}
                className="h-7 px-2.5 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {profileOnly && (
            <div className="ml-auto flex items-center gap-2">
              <GridColumnsControl gridColumns={gridColumns} setGridColumns={setGridColumns} maxColumns={maxColumns} />
              {isOwnProfile && (
                <Button
                  size="icon"
                  title={tt("newFolio")}
                  onClick={() => { setEditingCollection(null); setFormKind("personal"); setFormOpen(true) }}
                  className="h-8 w-8 rounded-full"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {view === "explore" && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setPeopleFocused(true)}
              onBlur={() => setTimeout(() => setPeopleFocused(false), 150)}
              placeholder={tt("searchFoliosByTitleOrTag")}
              className="pl-8 h-8 text-sm"
            />
            {/* Creators with at least one public Goonfolio: looking for a person means looking for their folios. */}
            <PeopleSearchDropdown query={searchInput} scope="goonfolios" open={peopleFocused} />
          </div>

          <div className="inline-flex rounded-lg border p-1 gap-1">
            {SORT_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={sort === opt.value ? "default" : "ghost"}
                onClick={() => setSort(opt.value)}
                className="h-7 px-2.5 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {user && (
            <Button
              size="sm"
              variant={followedOnly ? "default" : "outline"}
              onClick={() => setFollowedOnly((v) => !v)}
              className={cn("h-8 gap-1.5 text-xs", followedOnly && "bg-accent text-accent-foreground hover:bg-accent/90")}
            >
              <UserCheck className="h-3.5 w-3.5" />
              {tt("following")}
            </Button>
          )}

          <Button
            size="sm"
            variant={communityOnly ? "default" : "outline"}
            onClick={() => setCommunityOnly((v) => !v)}
            className={cn("h-8 gap-1.5 text-xs", communityOnly && "bg-accent text-accent-foreground hover:bg-accent/90")}
            aria-pressed={communityOnly}
          >
            <Users className="h-3.5 w-3.5" />
            {tc("community")}
          </Button>

          {user ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditingCollection(null); setFormKind("community"); setFormOpen(true) }}
              className="h-8 gap-1.5 text-xs ml-auto"
              data-testid="new-community-folio"
            >
              <Plus className="h-3.5 w-3.5" />
              {tc("newCommunityFolio")}
            </Button>
          ) : null}
        </div>
      )}

      {collections === null ? (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{tt("loadingFolios")}</span>
        </div>
      ) : collections.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Folder className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              {view === "explore"
                ? search
                  ? `No folios match "${search}"`
                  : followedOnly
                    ? "None of the creators you follow have public folios yet"
                    : "No public folios yet"
                : "No folios yet"}
            </p>
            {isOwnProfile && view === "folios" && (
              <Button onClick={() => { setEditingCollection(null); setFormKind("personal"); setFormOpen(true) }}>
                <Plus className="h-4 w-4 mr-2" />
                {tt("createYourFirstFolio")}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, gridColumns)}, minmax(0, 1fr))` }}
        >
          {(itemsWithAds ?? []).map((entry, index) => {
            if ("ad" in entry && entry.type === "ad") {
              return (
                <InlineGridAd
                  key={`ad-${entry.ad.id}-${index}`}
                  ad={entry.ad}
                  aspectRatio="square"
                  showDismiss
                  onDismiss={() => handleDismissAd(entry.ad.id)}
                />
              )
            }
            if (!("item" in entry)) return null
            const collection = entry.item
            return (
              <CollectionCard
                key={collection.id}
                collection={collection}
                isOwner={isOwnProfile && view === "folios"}
                onEdit={(c) => { setEditingCollection(c); setFormOpen(true) }}
                onDelete={handleDelete}
                onRate={(c) => setRatingCollection(c)}
              />
            )
          })}
        </div>
      )}

      <CollectionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        collection={editingCollection}
        onSaved={load}
        kind={formKind}
      />

      {ratingCollection && (
        <CollectionReviewTags
          collectionId={ratingCollection.id}
          open={!!ratingCollection}
          onOpenChange={(open) => !open && setRatingCollection(null)}
          onSubmitted={() => setRatingCollection(null)}
        />
      )}
    </div>
  )
}
